import React, { useEffect, useState, useCallback, useMemo } from "react";

import { useParams, useLocation, useHistory } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { Trans } from "react-i18next";

import Button from "@/components/atoms/Buttons/Button";
import CircularLoader from "@/components/atoms/CircularLoader";
import Footer from "@/components/molecules/Footer";
import Header from "@/components/molecules/Header";
import { ReactComponent as SuccessIcon } from "@/assets/images/success.svg";
import { ReactComponent as InvalidSofIcon } from "@/assets/images/invalid_sof.svg";
import { ReactComponent as CloseIcon } from "@/assets/images/close_circle.svg";

import { Title } from "@/components/atoms/Typography/styles";
import { LayoutWrapper } from "@/components/molecules/Wrappers/LayoutWrapper";

import i18n from "@/translate/i18n";
import { checkKycToken, uploadDocument } from "@/services/api";
import uploadGif from "@/assets/images/upload.gif";

const TIME_TO_REDIRECT = 3000;

const isValidUrl = (urlString) => {
  if (!urlString) return false;
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

function SOFValidationUpload() {
  const location = useLocation();
  const history = useHistory();
  const { kycLevel, customerId, token: apiToken } = useParams();

  const merchantName = location.state?.merchant?.name || "Merchant";
  const transactionId = location.state?.transactionId;
  const rawRedirectUrlSuccess = location.state?.redirect_urls?.success;
  const rawRedirectUrlFailed = location.state?.redirect_urls?.failed;
  const kycRequired = location.state?.kycRequired;

  const validRedirectUrlSuccess = useMemo(
    () => (isValidUrl(rawRedirectUrlSuccess) ? rawRedirectUrlSuccess : null),
    [rawRedirectUrlSuccess],
  );

  const validRedirectUrlFailed = useMemo(
    () => (isValidUrl(rawRedirectUrlFailed) ? rawRedirectUrlFailed : "#"),
    [rawRedirectUrlFailed],
  );

  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles?.length) {
      setFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      ]);
    }
  }, []);

  const removeFile = (e, fileName) => {
    e.stopPropagation();
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/bmp": [],
      "application/pdf": [],
    },
    multiple: true,
    noClick: files.length > 0,
    noKeyboard: true,
  });

  useEffect(
    () => () => files.forEach((file) => URL.revokeObjectURL(file.preview)),
    [files],
  );

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const formData = new FormData();

      files.forEach((file) => {
        formData.append("file", file);
      });
      formData.append("customer_id", customerId);
      formData.append("token", apiToken);
      formData.append("verification_type", "sof");

      await uploadDocument(formData);

      setShowSuccessMessage(true);

      setTimeout(() => {
        if (kycRequired && validRedirectUrlSuccess) {
          window.location.href = validRedirectUrlSuccess;
        } else if (transactionId) {
          history.push(
            `/transaction-details/${transactionId}/${apiToken}?source=merchant`,
          );
        } else if (validRedirectUrlSuccess) {
          window.location.href = validRedirectUrlSuccess;
        }
      }, TIME_TO_REDIRECT);
    } catch (err) {
      const message = err?.response?.data?.message;
      setError(i18n.t([`error.kyc.${message}`, "error.kyc.unspecific"]));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkToken = async () => {
      try {
        setIsLoading(true);
        const params = {
          kyc_level: kycLevel,
          customer_id: customerId,
          token: apiToken,
        };
        await checkKycToken(params);
      } catch (err) {
        const message = err?.response?.data?.message;
        setError(i18n.t([`error.${message}`, "error.unspecific"]));
      } finally {
        setIsLoading(false);
      }
    };

    checkToken();
  }, [apiToken, customerId, kycLevel]);

  return (
    <div className="flex justify-center items-center bg-white sm:bg-gray-900 font-arboria-book min-h-screen overflow-x-hidden">
      <div className="flex flex-col justify-between items-center w-[500px] rounded-[20px] overflow-hidden text-center">
        <Header title={i18n.t("header.return")} />
        <LayoutWrapper>
          {error ? (
            <>
              <InvalidSofIcon className="w-14 mt-8" />
              <div className="mt-8">
                <Title widthFull>{i18n.t("sofValidation.errorMessage")}</Title>
              </div>
              <h3 className="mt-5 font-medium text-md text-black max-w-[80%]">
                <Trans
                  i18nKey="sofValidation.errorDescription"
                  components={{
                    1: <span className="text-blue-600 underline font-medium" />,
                  }}
                />
              </h3>
              <a
                className="mt-20 w-full py-[0.73rem] border border-gray-600 rounded-[50px] bg-cripten-primary text-black cursor-pointer font-arboria-bold text-[calc(0.875em_+_0.2vh)] font-bold hover:brightness-[.85]"
                href={validRedirectUrlFailed}
              >
                {i18n.t("general.backTo", {
                  merchant: merchantName,
                })}
              </a>
            </>
          ) : isLoading ? (
            <div className="size-full">
              <CircularLoader />
            </div>
          ) : showSuccessMessage ? (
            <div className="flex flex-col items-center">
              <SuccessIcon className="w-14" />
              <div className="mt-8">
                <Title>{i18n.t("sofValidation.successMessage")}</Title>
              </div>
              <h3 className="mt-5 font-medium text-md text-black">
                {i18n.t("sofValidation.successInfo")}
              </h3>
            </div>
          ) : (
            <>
              <div className="mt-8">
                <Title widthFull>
                  {i18n.t("sofValidation.sendIncomeOfPoof")}
                </Title>
              </div>

              <div
                {...getRootProps()}
                className={`flex flex-col relative items-center gap-3 p-5 mt-6 rounded-[10px] text-[0.8rem] text-left group border-2 border-dashed transition-colors
                ${
                  isDragActive
                    ? "bg-cripten-dark/10 border-cripten-dark"
                    : "border-cripten-dark/40"
                }
                ${
                  files.length > 0
                    ? "bg-gray-50 border-solid cursor-default w-full"
                    : "cursor-pointer w-[80%]"
                }
              `}
              >
                <input {...getInputProps()} />

                {files.length === 0 ? (
                  <>
                    <img
                      alt="upload"
                      className="opacity-50"
                      style={{ width: "180px" }}
                      src={uploadGif}
                    />
                    <span className="leading-snug text-black/60 text-center">
                      <Trans
                        i18nKey="general.sofFileInstructions"
                        components={{ 1: <span className="font-bold" /> }}
                      />
                    </span>
                  </>
                ) : (
                  <div className="w-full grid grid-cols-2 gap-5 animate-fade-in pt-2">
                    {files.map((file) => (
                      <div
                        key={file.name}
                        className="relative w-full aspect-[3/4] group/item bg-gray-300 p-8 shadow-md rounded-md"
                      >
                        <button
                          type="button"
                          onClick={(e) => removeFile(e, file.name)}
                          className="absolute -top-3 -right-3 z-10"
                        >
                          <CloseIcon />
                        </button>

                        {file.type.includes("image") ? (
                          <img
                            src={file.preview}
                            alt={file.name}
                            className="w-full h-full object-cover shadow-md rounded-md"
                            onLoad={() => {
                              URL.revokeObjectURL(file.preview);
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-500 p-2 text-center">
                            <span className="text-xs font-bold break-all">
                              {file.name}
                            </span>
                            <span className="text-[10px] uppercase mt-1">
                              {file.name.split(".").pop()}
                            </span>
                          </div>
                        )}

                        <div className="w-full absolute left-0 bottom-0 flex justify-center items-center h-8 pointer-events-none">
                          <span className="text-gray-900 text-[10px] truncate px-2 w-[80%] text-center">
                            {file.name}
                          </span>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={open}
                      className="flex items-center justify-center aspect-[3/4] border-2 border-dashed border-cripten-dark/20 rounded-lg text-gray-400 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <span className="text-cripten-blue underline font-semibold text-xs">
                        {i18n.t("general.addMoreFiles")}
                      </span>
                    </button>
                  </div>
                )}

                {isDragActive && (
                  <div className="absolute bg-black/60 top-0 left-0 size-full flex items-center justify-center text-white text-lg z-20 animate-fade-in">
                    {i18n.t("sofValidation.dropHere")}
                  </div>
                )}
              </div>

              <div className="w-[80%]">
                <Button
                  type="button"
                  handleClick={files.length > 0 ? handleSubmit : open}
                >
                  {files.length > 0
                    ? i18n.t("sofValidation.buttonLabel")
                    : i18n.t("sofValidation.selectFile") ||
                      i18n.t("sofValidation.buttonLabel")}
                </Button>
              </div>
            </>
          )}
        </LayoutWrapper>

        <Footer />
      </div>
    </div>
  );
}

export default SOFValidationUpload;
